import { useState } from "react";
import { useMutation } from "urql";

import { UploadFileInput } from "@/components/graphql/schema.graphql";
import { FilepickerFile, Source } from "@/components/ui-v2/FilePicker/types";
import { UploadFile } from "@/features/drive/queries.graphql";
import { useRequiredWorkspace, useUrqlResponseHandler } from "@/hooks";
import { ContentType } from "@/types";
import { getObjectType } from "@/utils/libraryObjectUtils";
import { uploadFileToUrl } from "@/utils/upload";

type UseFileUploadInput = Pick<UploadFileInput, "sourceType">;

interface UseFileUploadReturn {
  upload: (file: File, input: UseFileUploadInput) => Promise<FilepickerFile>;
  isUploading: boolean;
}

export const useFileUpload = (): UseFileUploadReturn => {
  const { workspace } = useRequiredWorkspace();
  const { getResponseFromState } = useUrqlResponseHandler();
  const [, uploadFileMutation] = useMutation(UploadFile);
  const [isUploading, setIsUploading] = useState<boolean>(false);

  const upload = async (
    file: File,
    input: UseFileUploadInput,
  ): Promise<FilepickerFile> => {
    setIsUploading(true);

    try {
      const uploadFileResult = await uploadFileMutation({
        uploadFileInput: {
          ...input,
          name: file.name,
          contentType: file.type,
          sizeBytes: file.size,
          workspaceId: workspace.id,
        },
      });

      const {
        data: { uploadFile: uploadedFile },
        error,
      } = getResponseFromState({ state: uploadFileResult });

      if (error || !uploadedFile) {
        console.error("Failed to get upload URL", error);
        throw new Error("Failed to get upload URL");
      }

      if (!uploadedFile.uploadUrl) {
        console.error("No upload URL provided for file", file.name);
        throw new Error("Error during single file upload");
      }

      const objectType = getObjectType(uploadedFile);

      await uploadFileToUrl({
        signedUrl: uploadedFile.uploadUrl,
        contentType: uploadedFile.contentType,
        blob: file,
      });

      let width: number | undefined;
      let height: number | undefined;
      if (objectType === "image") {
        const imageBitmap = await createImageBitmap(file);
        height = imageBitmap.height;
        width = imageBitmap.width;
      }

      return {
        id: uploadedFile.id,
        src: uploadedFile.url,
        name: file.name,
        type: objectType,
        contentType: file.type as ContentType,
        thumbnailSrc: uploadedFile.url,
        interactedAt: new Date(),
        source: "upload" as Source,
        width,
        height,
      };
    } catch (error) {
      console.error("Error during single file upload:", file.name, error);
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  return {
    upload,
    isUploading,
  };
};
